import { useState, useEffect } from 'react'
import { get } from '../utils/api'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export default function PacePage() {
  const [data, setData] = useState(null)
  const [year, setYear] = useState(new Date().getFullYear())
  const [loading, setLoading] = useState(true)

  useEffect(() => { setLoading(true); get(`/pace?year=${year}`).then(setData).finally(() => setLoading(false)) }, [year])

  const fmt = (n) => '$' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
  const getMonth = (rows, m) => rows?.find(r => parseInt(r.month) === m) || { events: 0, room_nights: 0, fb_revenue: 0, room_revenue: 0 }

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>

  const thisYear = data?.thisYear || []
  const lastYear = data?.lastYear || []
  const lostBusiness = data?.lostBusiness || []

  // Totals
  const tyTotals = { events: 0, room_nights: 0, fb: 0, room: 0 }
  const lyTotals = { events: 0, room_nights: 0, fb: 0, room: 0 }
  MONTHS.forEach((_, i) => {
    const ty = getMonth(thisYear, i + 1)
    const ly = getMonth(lastYear, i + 1)
    tyTotals.events += parseInt(ty.events || 0); tyTotals.room_nights += parseInt(ty.room_nights || 0)
    tyTotals.fb += parseFloat(ty.fb_revenue || 0); tyTotals.room += parseFloat(ty.room_revenue || 0)
    lyTotals.events += parseInt(ly.events || 0); lyTotals.room_nights += parseInt(ly.room_nights || 0)
    lyTotals.fb += parseFloat(ly.fb_revenue || 0); lyTotals.room += parseFloat(ly.room_revenue || 0)
  })
  const maxRN = Math.max(...MONTHS.map((_, i) => Math.max(parseInt(getMonth(thisYear, i+1).room_nights||0), parseInt(getMonth(lastYear, i+1).room_nights||0))), 1)

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="mb-0">Pace Report</h2>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-ghost-secondary btn-sm" onClick={() => setYear(year - 1)}>&laquo;</button>
          <span className="fw-bold" style={{ fontSize: '1.1rem', minWidth: 50, textAlign: 'center' }}>{year}</span>
          <button className="btn btn-ghost-secondary btn-sm" onClick={() => setYear(year + 1)}>&raquo;</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Events', ty: tyTotals.events, ly: lyTotals.events, format: v => v },
          { label: 'Room Nights', ty: tyTotals.room_nights, ly: lyTotals.room_nights, format: v => v.toLocaleString() },
          { label: 'Room Revenue', ty: tyTotals.room, ly: lyTotals.room, format: fmt },
          { label: 'F&B Revenue', ty: tyTotals.fb, ly: lyTotals.fb, format: fmt },
        ].map(({ label, ty, ly, format }) => {
          const pct = ly > 0 ? ((ty - ly) / ly * 100).toFixed(1) : ty > 0 ? 100 : 0
          return (
            <div key={label} className="col-md-3">
              <div className="card">
                <div className="card-body py-3 text-center">
                  <div className="text-secondary" style={{ fontSize: '0.8rem' }}>{label}</div>
                  <div className="fw-bold" style={{ fontSize: '1.3rem' }}>{format(ty)}</div>
                  <div style={{ fontSize: '0.8rem' }}>
                    <span className="text-secondary">vs {year - 1}: {format(ly)}</span>
                    {' '}
                    <span className={parseFloat(pct) >= 0 ? 'text-green' : 'text-red'}>{parseFloat(pct) >= 0 ? '+' : ''}{pct}%</span>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Visual Bar Chart */}
      <div className="card mb-4">
        <div className="card-header"><h3 className="card-title mb-0">Room Nights by Month</h3></div>
        <div className="card-body">
          <div className="d-flex align-items-end gap-1" style={{ height: 200 }}>
            {MONTHS.map((m, i) => {
              const ty = parseInt(getMonth(thisYear, i + 1).room_nights || 0)
              const ly = parseInt(getMonth(lastYear, i + 1).room_nights || 0)
              return (
                <div key={m} className="text-center" style={{ flex: 1 }}>
                  <div className="d-flex align-items-end justify-content-center gap-1" style={{ height: 170 }}>
                    <div style={{ width: '35%', height: `${(ly / maxRN) * 160}px`, background: '#e2e8f0', borderRadius: '3px 3px 0 0', minHeight: ly > 0 ? 4 : 0 }} title={`${year-1}: ${ly}`}></div>
                    <div style={{ width: '35%', height: `${(ty / maxRN) * 160}px`, background: '#206bc4', borderRadius: '3px 3px 0 0', minHeight: ty > 0 ? 4 : 0 }} title={`${year}: ${ty}`}></div>
                  </div>
                  <div style={{ fontSize: '0.7rem', marginTop: 4 }} className="text-secondary">{m}</div>
                </div>
              )
            })}
          </div>
          <div className="d-flex justify-content-center gap-4 mt-3" style={{ fontSize: '0.8rem' }}>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#e2e8f0', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }}></span>{year - 1}</span>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#206bc4', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }}></span>{year}</span>
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="card mb-4">
        <div className="card-header"><h3 className="card-title mb-0">Monthly Breakdown</h3></div>
        <div className="table-responsive">
          <table className="table table-vcenter card-table table-striped">
            <thead>
              <tr><th>Month</th><th className="text-end">Events</th><th className="text-end">Rm Nights</th><th className="text-end">Rm Rev</th><th className="text-end">F&B Rev</th><th className="text-end">Total</th><th className="text-end">vs {year-1}</th></tr>
            </thead>
            <tbody>
              {MONTHS.map((m, i) => {
                const ty = getMonth(thisYear, i + 1)
                const ly = getMonth(lastYear, i + 1)
                const tyTotal = parseFloat(ty.room_revenue || 0) + parseFloat(ty.fb_revenue || 0)
                const lyTotal = parseFloat(ly.room_revenue || 0) + parseFloat(ly.fb_revenue || 0)
                const pct = lyTotal > 0 ? ((tyTotal - lyTotal) / lyTotal * 100).toFixed(1) : '--'
                return (
                  <tr key={m}>
                    <td className="fw-medium">{m}</td>
                    <td className="text-end">{ty.events}</td>
                    <td className="text-end">{parseInt(ty.room_nights || 0).toLocaleString()}</td>
                    <td className="text-end">{fmt(ty.room_revenue)}</td>
                    <td className="text-end">{fmt(ty.fb_revenue)}</td>
                    <td className="text-end fw-medium">{fmt(tyTotal)}</td>
                    <td className="text-end"><span className={pct !== '--' && parseFloat(pct) >= 0 ? 'text-green' : pct !== '--' ? 'text-red' : ''}>{pct !== '--' ? (parseFloat(pct) >= 0 ? '+' : '') + pct + '%' : '--'}</span></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lost Business */}
      {lostBusiness.length > 0 && (
        <div className="card">
          <div className="card-header"><h3 className="card-title mb-0">Lost Business</h3></div>
          <div className="table-responsive">
            <table className="table table-vcenter card-table">
              <thead><tr><th>Month</th><th>Reason</th><th className="text-end">Count</th><th className="text-end">Value</th></tr></thead>
              <tbody>
                {lostBusiness.map((lb, i) => (
                  <tr key={i}><td>{MONTHS[parseInt(lb.month) - 1]}</td><td>{lb.lost_reason || 'Not specified'}</td><td className="text-end">{lb.count}</td><td className="text-end text-red">{fmt(lb.lost_value)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
